import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { ScrollView, View } from 'react-native';
import { useMutation } from '@tanstack/react-query';
import { useQuery } from '@apollo/client/react';
import { CurrentUserDocument } from '~/graphql/generated/graphql';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/date-picker/DatePicker';
import { useHistoryMinDate } from '~/calendar/hooks/useHistoryMinDate';
import { WorkoutFormActions } from '~/workout/components/workout-form-actions/WorkoutFormActions';
import { WorkoutStructure } from '~/workout/components/workout-structure/WorkoutStructure';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type { WorkoutRecord } from '~/powersync/generated/schema';
import { useEffect } from 'react';
import { formatISO, parseISO } from 'date-fns';
import { WorkoutMobileHeader } from '~/workout/components/workout-mobile-header/WorkoutMobileHeader';

export interface WorkoutFormProps {
  workoutNiceId?: string;
  initialDate?: Date;
}

interface WorkoutFormData {
  name: string;
  plannedAt: Date;
  duration: number;
  note: string;
}

export const WorkoutForm = ({
  workoutNiceId,
  initialDate,
}: WorkoutFormProps) => {
  const { t } = useTranslation('workout');
  const minimumDate = useHistoryMinDate();
  const router = useRouter();
  const powerSync = usePowerSync();

  const { data: userData } = useQuery(CurrentUserDocument);
  const currentUserId = userData?.currentUser?.id;

  const isEditMode = !!workoutNiceId;

  const { data: existingWorkout, isLoading: loadingWorkout } =
    useLivePowerSyncQuery<WorkoutRecord>(
      workoutNiceId ? db.Workout.queries.findByNiceId(workoutNiceId) : null,
      [workoutNiceId],
      {
        enabled: isEditMode && !!workoutNiceId,
        single: true,
      },
    );

  const saveWorkoutMutation = useMutation({
    mutationFn: (data: {
      name: string;
      duration: number;
      plannedAt: string;
      note?: string;
    }) => {
      if (isEditMode && existingWorkout) {
        return db.Workout.mutations.update(powerSync, existingWorkout.id, {
          name: data.name,
          duration: data.duration,
          plannedAt: data.plannedAt,
          note: data.note,
        });
      } else {
        return db.Workout.mutations.create(powerSync, {
          name: data.name,
          duration: data.duration,
          plannedAt: data.plannedAt,
          userId: currentUserId!,
        });
      }
    },
  });

  const plannedAt = initialDate || new Date();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<WorkoutFormData>({
    defaultValues: {
      name: formatISO(plannedAt, { representation: 'date' }),
      plannedAt,
      duration: 60,
      note: '',
    },
  });

  // In create mode the desktop header date picker drives the planned date
  // via the `initialDate` route param; keep the form value in sync with it.
  useEffect(() => {
    if (!isEditMode && initialDate) {
      setValue('plannedAt', initialDate);
    }
  }, [initialDate, isEditMode, setValue]);

  useEffect(() => {
    if (existingWorkout && isEditMode) {
      const plannedAt = existingWorkout.plannedAt
        ? parseISO(existingWorkout.plannedAt.slice(0, 10))
        : new Date();
      reset({
        name:
          existingWorkout.name ||
          formatISO(plannedAt, { representation: 'date' }),
        plannedAt,
        duration: existingWorkout.duration || 60,
        note: existingWorkout.note || '',
      });
    }
  }, [existingWorkout, isEditMode, reset]);

  const onSubmit = async (data: WorkoutFormData) => {
    saveWorkoutMutation.mutate(
      {
        name: data.name,
        plannedAt: formatISO(data.plannedAt, { representation: 'date' }),
        duration: data.duration,
        note: data.note,
      },
      {
        onSuccess: () => {
          router.back();
        },
      },
    );
  };

  const handleCancel = () => {
    router.back();
  };

  return (
    <ScrollView
      className="flex-1 bg-background"
      keyboardShouldPersistTaps="handled"
    >
      <WorkoutMobileHeader
        title={
          isEditMode
            ? existingWorkout?.name || t('form.title.edit')
            : t('form.title.create')
        }
        workout={existingWorkout}
        className="md:hidden"
      />
      <View className="p-4 gap-4">
        {/* Planned date: mobile only — desktop uses the header date picker */}
        <View className="gap-2 md:hidden">
          <Controller
            control={control}
            name="plannedAt"
            rules={{
              required: t('form.validation.dateRequired'),
            }}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                value={value}
                onChange={onChange}
                label={t('form.fields.plannedAt.label')}
                minimumDate={minimumDate ?? undefined}
              />
            )}
          />
          {errors.plannedAt && (
            <Text className="text-destructive text-xs">
              {errors.plannedAt.message}
            </Text>
          )}
        </View>
        <Card className="bg-surface rounded-xl p-4 gap-4">
          <View className="gap-4">
            <View className="gap-2">
              <Text className="text-xs font-medium text-text-secondary">
                {t('form.fields.name.label')}
              </Text>
              <Controller
                control={control}
                name="name"
                rules={{
                  required: t('form.validation.nameRequired'),
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input
                    testID="workout-name-input"
                    placeholder={t('form.fields.name.placeholder')}
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    className="bg-surface-elevated rounded-lg px-3 py-3 border-0 text-sm font-regular text-text-primary"
                    placeholderClassName="text-text-muted"
                  />
                )}
              />
              {errors.name && (
                <Text className="text-destructive text-xs">
                  {errors.name.message}
                </Text>
              )}
            </View>

            <View className="gap-2">
              <Text className="text-xs font-medium text-text-secondary">
                {t('form.fields.note.label')}
              </Text>
              <Controller
                control={control}
                name="note"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input
                    placeholder={t('form.fields.note.placeholder')}
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    multiline
                    className="bg-surface-elevated rounded-lg px-3 py-3 border-0 text-sm font-regular text-text-primary"
                    placeholderClassName="text-text-muted"
                  />
                )}
              />
            </View>

            <View className="flex-row gap-3 mt-2">
              <Button
                variant="secondary"
                className="flex-1"
                onPress={handleCancel}
                disabled={saveWorkoutMutation.isPending}
              >
                <Text>{t('form.buttons.cancel')}</Text>
              </Button>
              <Button
                testID="workout-create-submit"
                className="flex-1"
                onPress={handleSubmit(onSubmit)}
                loading={saveWorkoutMutation.isPending}
              >
                <Text>
                  {isEditMode
                    ? t('form.buttons.update')
                    : t('form.buttons.create')}
                </Text>
              </Button>
            </View>
          </View>
        </Card>

        {existingWorkout && (
          <>
            <WorkoutStructure
              workoutId={existingWorkout.id}
              isLoading={saveWorkoutMutation.isPending}
            />

            <WorkoutFormActions
              isEditMode={true}
              isLoading={saveWorkoutMutation.isPending}
              workoutId={existingWorkout.id}
            />
          </>
        )}
      </View>
    </ScrollView>
  );
};
